import { useCallback, useMemo } from 'react';
import { miembroService } from '../services/miembroService';
import { ComboboxLazyOption } from '../components/ui/ComboboxLazy';

/**
 * Hook personalizado para búsqueda de miembros con caché y optimización
 * Elimina código duplicado en múltiples componentes
 */
export const useMiembroSearch = (lumisialId: string | null) => {
  // Cache simple para búsquedas repetidas
  const searchCache = useMemo(() => new Map<string, ComboboxLazyOption[]>(), []);

  const handleSearch = useCallback(async (searchTerm: string): Promise<ComboboxLazyOption[]> => {
    if (!lumisialId || !searchTerm.trim()) {
      return [];
    }

    // Revisar caché primero
    const cacheKey = `${lumisialId}-${searchTerm.toLowerCase()}`;
    if (searchCache.has(cacheKey)) {
      return searchCache.get(cacheKey)!;
    }

    try {
      const results = await miembroService.searchByLumisial(lumisialId, searchTerm, 50);
      
      const options: ComboboxLazyOption[] = results.map(miembro => ({
        value: miembro.id,
        label: `${miembro.nombres} ${miembro.apellidos}`,
        subtitle: miembro.rut_pasaporte || '',
        data: miembro
      }));

      // Guardar en caché (limitar a 100 entradas para evitar memory leaks)
      if (searchCache.size >= 100) {
        const firstKey = searchCache.keys().next().value;
        if (firstKey) {
          searchCache.delete(firstKey);
        }
      }
      searchCache.set(cacheKey, options);

      return options;
    } catch (error) {
      console.error('Error buscando miembros:', error);
      return [];
    }
  }, [lumisialId, searchCache]);

  // Función para limpiar caché (útil cuando se cambia de lumisial)
  const clearCache = useCallback(() => {
    searchCache.clear();
  }, [searchCache]);

  return {
    handleSearch,
    clearCache,
    canSearch: !!lumisialId
  };
};
